import React, { useState, Fragment, useEffect } from "react";
import apiHandler from "../../../js/apiHandler";
import LoadingSpinner from "../../global/LoadingSpinner";
import {
	isValidEmail,
	isValidPassword,
	passwordsMatch,
	normalizeEmail,
	formatPhoneNumber,
	formatPhoneForApi,
	formatMessage,
} from "../../../js/formUtils";

/**
 * CreateAccount Component
 *
 * Props:
 * @param {function} onCancel - Callback function to return to login
 * @param {boolean} isInitialView - Whether this is the initial/invite view for the user
 */

function CreateAccount(props) {
	const [error, setError] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [isLoadingPmsOptions, setIsLoadingPmsOptions] = useState(true);

	// Create Account
	const [firstName, setFirstName] = useState("");
	const [lastName, setLastName] = useState("");
	const [email, setEmail] = useState("");
	const [phoneNumber, setPhoneNumber] = useState("");
	const [password, setPassword] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [createOrganization, setCreateOrganization] = useState(false);
	const [pmsProvider, setPmsProvider] = useState("no");
	const [clientCode, setClientCode] = useState("");
	const [organizationTitle, setOrganizationTitle] = useState("");
	const [accountTier, setAccountTier] = useState("");
	const [generateGuestbook, setGenerateGuestbook] = useState(true);
	const [hsId, setHsId] = useState("");
	const [organizationType, setOrganizationType] = useState("");
	const [showAllFields, setShowAllFields] = useState(false);
	const [prefilledIdentityFields, setPrefilledIdentityFields] = useState({
		firstName: false,
		lastName: false,
		email: false,
		phoneNumber: false,
		organizationTitle: false,
	});

	// PMS Integration state
	const [pmsOptions, setPmsOptions] = useState([]);
	const [pmsFormValues, setPmsFormValues] = useState({});
	const [activeTab, setActiveTab] = useState("required");

	// Autogenerate properties state
	const [autogenerateItems, setAutogenerateItems] = useState([]);
	const [showAddItem, setShowAddItem] = useState(false);
	const [addItemError, setAddItemError] = useState("");
	const [newItem, setNewItem] = useState({
		airbnbUrl: "",
		vrboUrl: "",
		address1: "",
		address2: "",
		city: "",
		state: "",
		postalCode: "",
		country: "USA",
	});

	const [hidePmsSelect, setHidePmsSelect] = useState(true);

	// Handle props
	useEffect(() => {
		if (props.firstName) {
			setFirstName(props.firstName);
			setPrefilledIdentityFields((prev) => ({ ...prev, firstName: true }));
		}
		if (props.lastName) {
			setLastName(props.lastName);
			setPrefilledIdentityFields((prev) => ({ ...prev, lastName: true }));
		}
		if (props.email) {
			setEmail(props.email);
			setPrefilledIdentityFields((prev) => ({ ...prev, email: true }));
		}
	}, [props.firstName, props.lastName, props.email]);

	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		const createOrganizationParam = params.get("create_organization");
		const normalizedCreateOrganization = createOrganizationParam
			? String(createOrganizationParam).toLowerCase()
			: "";
		const shouldCreateOrganization =
			!!createOrganizationParam &&
			normalizedCreateOrganization !== "0" &&
			normalizedCreateOrganization !== "false";

		setCreateOrganization(shouldCreateOrganization);

		const integrationParam = params.get("integration");
		const clientCodeParam = params.get("client_code");

		// Collect all params for PMS pre-population
		const pmsValues = {};
		const excludedKeys = ["create_organization", "integration"];
		for (const [key, value] of params.entries()) {
			if (!excludedKeys.includes(key)) {
				pmsValues[key] = value;
			}
		}

		// Pre-populate PMS form values with all available URL params
		if (Object.keys(pmsValues).length > 0) {
			setPmsFormValues((prev) => ({ ...prev, ...pmsValues }));
		}

		if (clientCodeParam) {
			setClientCode(clientCodeParam);
		}

		// Logic for create_organization=1
		if (normalizedCreateOrganization === "1") {
			setHidePmsSelect(true);
			if (integrationParam) {
				setPmsProvider(integrationParam);
			} else {
				setPmsProvider("auto_generate");
			}
		}
		// Logic for create_organization=2 or other truthy values
		else if (shouldCreateOrganization) {
			setHidePmsSelect(false);

			if (integrationParam) {
				setPmsProvider(integrationParam);
			} else if (clientCodeParam) {
				setPmsProvider("barefoot");
			} else {
				setPmsProvider("auto_generate");
			}
		}

		const organizationTitleParam = params.get("organization_title");
		if (organizationTitleParam) {
			setOrganizationTitle(organizationTitleParam);
			setPrefilledIdentityFields((prev) => ({
				...prev,
				organizationTitle: true,
			}));
		}

		const accountTierParam = params.get("account_tier");
		if (accountTierParam) {
			setAccountTier(accountTierParam);
		}

		const generateGuestbookParam = params.get("generate_guestbook");
		if (generateGuestbookParam !== null) {
			setGenerateGuestbook(
				generateGuestbookParam !== "false" && generateGuestbookParam !== "0",
			);
		}

		const hsIdParam = params.get("hs_id");
		if (hsIdParam) {
			setHsId(hsIdParam);
		}

		const organizationTypeParam = params.get("organization_type");
		if (organizationTypeParam) {
			setOrganizationType(organizationTypeParam);
		}

		const firstNameParam = params.get("first_name");
		if (firstNameParam) {
			setFirstName(firstNameParam);
			setPrefilledIdentityFields((prev) => ({ ...prev, firstName: true }));
		}

		const lastNameParam = params.get("last_name");
		if (lastNameParam) {
			setLastName(lastNameParam);
			setPrefilledIdentityFields((prev) => ({ ...prev, lastName: true }));
		}

		const phoneNumberParam = params.get("phone_number");
		if (phoneNumberParam) {
			setPhoneNumber(phoneNumberParam);
			setPrefilledIdentityFields((prev) => ({ ...prev, phoneNumber: true }));
		}
	}, []);

	useEffect(() => {
		if (!createOrganization) {
			setIsLoadingPmsOptions(false);
			return;
		}

		const fetchPmsOptions = async () => {
			try {
				const response = await apiHandler.get({
					apiName: "itapi",
					path: "/manual-integrations",
				});
				if (response) {
					// apiHandler.get returns { data: ... }
					// if data is the array we want, use it.
					// Sometimes apiHandler returns data directly or wrapper
					// Let's assume response.data is the JSON body parsed.
					const data = response.data || response;
					setPmsOptions(Array.isArray(data) ? data : []);
				} else {
					console.error("Failed to fetch PMS options");
				}
			} catch (err) {
				console.error("Error fetching PMS options:", err);
			} finally {
				setIsLoadingPmsOptions(false);
			}
		};
		fetchPmsOptions();
	}, [createOrganization]);

	useEffect(() => {
		if (pmsProvider && pmsOptions.length > 0) {
			const selectedPms = pmsOptions.find((p) => p.name === pmsProvider);
			if (selectedPms && selectedPms.sections) {
				const defaults = {};
				selectedPms.sections.forEach((section) => {
					if (section.fields) {
						section.fields.forEach((field) => {
							if (field.default_value) {
								defaults[field.name] = field.default_value;
							}
						});
					}
				});
				if (Object.keys(defaults).length > 0) {
					setPmsFormValues((prev) => ({ ...prev, ...defaults }));
				}
			}
		}
	}, [pmsProvider, pmsOptions]);

	const getSelectedPms = () => {
		if (!pmsOptions || !pmsProvider) return null;
		return pmsOptions.find((p) => p.name === pmsProvider);
	};

	const handlePmsInputChange = (fieldName, value) => {
		setPmsFormValues((prev) => ({
			...prev,
			[fieldName]: value,
		}));
	};

	const renderPmsFields = (required) => {
		const selectedPms = getSelectedPms();
		if (!selectedPms) return null;

		const content = selectedPms.sections.map((section, sIndex) => {
			const fieldsToRender = section.fields
				? section.fields.filter((field) =>
						required ? field.required : !field.required,
					)
				: [];

			if (fieldsToRender.length === 0) return null;

			return (
				<Fragment key={sIndex}>
					{section.name && (
						<h5
							style={{
								marginTop: "15px",
								marginBottom: "10px",
								fontSize: "14px",
								fontWeight: "600",
								color: "#333",
							}}>
							{section.name}
						</h5>
					)}
					{section.description && required && (
						<p
							style={{
								fontSize: "12px",
								color: "#666",
								marginBottom: "10px",
							}}>
							{section.description}
						</p>
					)}
					{fieldsToRender.map((field, fIndex) => (
						<div className="form-group" key={fIndex}>
							<input
								type="text"
								id={field.name}
								value={pmsFormValues[field.name] || ""}
								placeholder=" "
								onChange={(e) =>
									handlePmsInputChange(field.name, e.currentTarget.value)
								}
								required={field.required}
								disabled={isSubmitting}
							/>
							<label htmlFor={field.name}>
								{field.title}
								{field.required ? "*" : ""}
							</label>
						</div>
					))}
				</Fragment>
			);
		});

		// Check if content has any non-null elements
		const hasContent = content.some((c) => c !== null);
		if (!hasContent) {
			return (
				<p style={{ padding: "20px", color: "#777", fontStyle: "italic" }}>
					No {required ? "required" : "advanced"} settings for this integration.
				</p>
			);
		}

		return content;
	};

	// Form validation using shared utilities
	const isEmailValid = () => isValidEmail(email);

	const isPmsConfigValid = () => {
		if (pmsProvider === "no" || pmsProvider === "auto_generate") return true;

		const selectedPms = getSelectedPms();
		if (selectedPms) {
			if (selectedPms.sections) {
				for (const section of selectedPms.sections) {
					if (section.fields) {
						for (const field of section.fields) {
							if (field.required) {
								const val = pmsFormValues[field.name];
								if (!val || val.trim().length === 0) return false;
							}
						}
					}
				}
			}
			return true;
		}

		// Fallback for hardcoded barefoot
		if (pmsProvider === "barefoot") {
			return clientCode.trim().length > 0;
		}

		return false;
	};

	const isFormValid = () =>
		isEmailValid() &&
		password.length > 0 &&
		passwordsMatch(password, confirmPassword) &&
		(!createOrganization || isPmsConfigValid());

	const handleAddItemSubmit = () => {
		// Validation for required fields
		if (
			!newItem.address1.trim() ||
			!newItem.city.trim() ||
			!newItem.state.trim() ||
			!newItem.country.trim() ||
			!newItem.postalCode.trim()
		) {
			setAddItemError(
				"Address 1, City, State/Province, Postal Code, and Country are required.",
			);
			return;
		}

		setAutogenerateItems([...autogenerateItems, { ...newItem }]);

		// Reset and close
		setNewItem({
			airbnbUrl: "",
			vrboUrl: "",
			address1: "",
			address2: "",
			city: "",
			state: "",
			postalCode: "",
			country: "USA",
		});
		setAddItemError("");
		setShowAddItem(false);
	};

	const handleRemoveItem = (index) => {
		const newItems = [...autogenerateItems];
		newItems.splice(index, 1);
		setAutogenerateItems(newItems);
	};

	const updateNewItemField = (field, value) => {
		setNewItem((prev) => ({ ...prev, [field]: value }));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!isFormValid()) {
			setError("Please fill out all required fields correctly.");
			return;
		}

		setIsSubmitting(true);
		setError("");

		// Prepare user attributes
		const attributes = {
			first_name: firstName,
			last_name: lastName,
			name: `${firstName} ${lastName}`,
		};

		// Add phone number if provided
		if (phoneNumber) {
			// Format phone for API using shared utility
			attributes["phone_number"] = formatPhoneForApi(phoneNumber);
		}

		// Trim attribute values
		const trimmedAttributes = {};
		Object.entries(attributes).forEach(([key, value]) => {
			trimmedAttributes[key] = typeof value === "string" ? value.trim() : value;
		});

		// Prepare the request body with required fields
		const createAccountRequestBody = {
			email: email.toLowerCase(),
			password,
			...trimmedAttributes,
		};

		if (createOrganization) {
			let providerConfiguration = {};

			const selectedPms = getSelectedPms();

			if (selectedPms) {
				selectedPms.sections.forEach((section) => {
					if (section.fields) {
						section.fields.forEach((field) => {
							if (pmsFormValues[field.name]) {
								providerConfiguration[field.name] =
									pmsFormValues[field.name].trim();
							}
						});
					}
				});
			} else if (pmsProvider === "barefoot") {
				providerConfiguration = {
					client_code: clientCode.trim(),
				};
			} else if (pmsProvider === "auto_generate") {
				providerConfiguration = {
					properties: autogenerateItems.map((item) => {
						return {
							airbnb_url: item.airbnbUrl || "",
							vrbo_url: item.vrboUrl || "",
							street_address: item.address1 || "",
							street_address2: item.address2 || "",
							city: item.city || "",
							province: item.state || "",
							postal_code: item.postalCode || "",
							country: item.country || "",
						};
					}),
				};
			}

			if (hsId) {
				providerConfiguration.hs_id = hsId.trim();
			}

			if (organizationType) {
				providerConfiguration.organization_type = organizationType.trim();
			}

			createAccountRequestBody.create_organization = {
				property_provider: pmsProvider,
				organization_title: organizationTitle.trim(),
				provider_configuration: providerConfiguration,
				generate_guestbook: generateGuestbook,
			};

			if (accountTier) {
				createAccountRequestBody.create_organization.account_tier =
					accountTier.trim();
			}
		}

		try {
			const createAccountResponse = await apiHandler.post({
				apiName: "accapi",
				path: "/user-signups",
				options: {
					body: createAccountRequestBody,
				},
			});

			const createAccountData =
				createAccountResponse?.data || createAccountResponse || {};
			const organizationRolesResponse =
				createAccountData?.organization_roles || [];
			const nextStepResponse = createAccountData?.next_step || null;

			setIsSubmitting(false);
			props.onAccountCreated(
				firstName,
				lastName,
				email,
				phoneNumber,
				password,
				organizationRolesResponse,
				nextStepResponse,
				createAccountData?.organization_creation_status_url,
			);
		} catch (error) {
			// Extract meaningful error message
			let errorMessage =
				error.message || "Failed to create account. Please try again.";

			// This paticular API returns a string encoded JSON object
			// Decode JSON from error.response.body if present
			let status = error.response?.statusCode || error.response?.status;
			let data = null;

			if (error.response?.body && typeof error.response.body === "string") {
				try {
					data = JSON.parse(error.response.body);
				} catch (e) {
					console.error("Failed to parse error response body:", e);
				}
			}

			// Handle 409 Conflict - user already exists
			if (status === 409) {
				errorMessage =
					"An account with this email already exists. Please try logging in instead.";
			}
			// Handle other error responses with the tuple format
			else if (data && data.error) {
				errorMessage = formatMessage(data.error);
			}

			setError(errorMessage);
			setIsSubmitting(false);
		}
	};

	const handlePhoneInput = (e) => {
		const input = e.currentTarget;
		const formattedNumber = formatPhoneNumber(input.value);
		input.value = formattedNumber;
		setPhoneNumber(formattedNumber);
	};

	const handleEmailInput = (value) => {
		const normalized = normalizeEmail(value);
		setEmail(normalized);
		return normalized;
	};

	const renderAddItemForm = () => (
		<Fragment>
			<div
				style={{
					padding: "15px",
					borderBottom: "1px solid #e0e0e0",
					backgroundColor: "#f9f9f9",
				}}>
				<h5
					style={{
						margin: 0,
						fontSize: "14px",
						fontWeight: "600",
						color: "#333",
					}}>
					Add Property Details
				</h5>
			</div>
			<div style={{ padding: "20px" }}>
				{addItemError && (
					<div className="field-error" style={{ marginBottom: "10px" }}>
						{addItemError}
					</div>
				)}
				<div className="form-group">
					<input
						type="text"
						id="airbnbUrl"
						placeholder=" "
						value={newItem.airbnbUrl}
						onChange={(e) => updateNewItemField("airbnbUrl", e.target.value)}
					/>
					<label htmlFor="airbnbUrl">Airbnb URL</label>
				</div>

				<div className="form-group">
					<input
						type="text"
						id="vrboUrl"
						placeholder=" "
						value={newItem.vrboUrl}
						onChange={(e) => updateNewItemField("vrboUrl", e.target.value)}
					/>
					<label htmlFor="vrboUrl">VRBO URL</label>
				</div>

				<hr
					style={{
						margin: "15px 0",
						borderTop: "1px solid #e0e0e0",
						borderBottom: "none",
					}}
				/>

				<div className="form-group">
					<input
						type="text"
						id="address1"
						placeholder=" "
						value={newItem.address1}
						onChange={(e) => updateNewItemField("address1", e.target.value)}
						required
					/>
					<label htmlFor="address1">Address 1*</label>
				</div>

				<div className="form-group">
					<input
						type="text"
						id="address2"
						placeholder=" "
						value={newItem.address2}
						onChange={(e) => updateNewItemField("address2", e.target.value)}
					/>
					<label htmlFor="address2">Address 2</label>
				</div>

				<div
					style={{
						display: "flex",
						gap: "10px",
						marginBottom: "0px",
					}}>
					<div className="form-group" style={{ flex: 1, margin: 0 }}>
						<input
							type="text"
							id="city"
							placeholder=" "
							value={newItem.city}
							onChange={(e) => updateNewItemField("city", e.target.value)}
							required
						/>
						<label htmlFor="city">City*</label>
					</div>
					<div className="form-group" style={{ flex: 1, margin: 0 }}>
						<input
							type="text"
							id="state"
							placeholder=" "
							value={newItem.state}
							onChange={(e) => updateNewItemField("state", e.target.value)}
							required
						/>
						<label htmlFor="state">State/Province*</label>
					</div>
				</div>
				<div
					style={{
						display: "flex",
						gap: "10px",
						marginBottom: "0px",
						marginTop: "20px",
					}}>
					<div className="form-group" style={{ flex: 1, margin: 0 }}>
						<input
							type="text"
							id="postalCode"
							placeholder=" "
							value={newItem.postalCode}
							onChange={(e) => updateNewItemField("postalCode", e.target.value)}
							required
						/>
						<label htmlFor="postalCode">Postal Code*</label>
					</div>
					<div className="form-group" style={{ flex: 1, margin: 0 }}>
						<input
							type="text"
							id="country"
							placeholder=" "
							value={newItem.country}
							onChange={(e) => updateNewItemField("country", e.target.value)}
							required
						/>
						<label htmlFor="country">Country*</label>
					</div>
				</div>

				<div
					style={{
						marginTop: "20px",
						display: "flex",
						justifyContent: "flex-end",
						gap: "10px",
					}}>
					<button
						type="button"
						onClick={() => {
							setShowAddItem(false);
							setAddItemError("");
						}}
						style={{
							padding: "8px 16px",
							background: "white",
							border: "1px solid #ccc",
							borderRadius: "4px",
							cursor: "pointer",
						}}>
						Cancel
					</button>
					<button
						type="button"
						onClick={handleAddItemSubmit}
						style={{
							padding: "8px 16px",
							background: "#007bff",
							color: "white",
							border: "none",
							borderRadius: "4px",
							cursor: "pointer",
						}}>
						Add Property
					</button>
				</div>
			</div>
		</Fragment>
	);

	const showPmsSidePanel =
		createOrganization &&
		(getSelectedPms() || (pmsProvider === "auto_generate" && showAddItem));

	useEffect(() => {
		if (props.onLayoutChange) {
			props.onLayoutChange(!!showPmsSidePanel);
		}
	}, [showPmsSidePanel, props.onLayoutChange]);

	const hidePrepopulatedIdentityFields =
		!showAllFields &&
		prefilledIdentityFields.firstName &&
		prefilledIdentityFields.lastName &&
		prefilledIdentityFields.email &&
		!!firstName &&
		!!lastName &&
		!!email &&
		isEmailValid();

	const hidePrepopulatedPhoneField =
		!showAllFields && prefilledIdentityFields.phoneNumber && !!phoneNumber;

	const hidePrepopulatedOrganizationTitleField =
		!showAllFields &&
		prefilledIdentityFields.organizationTitle &&
		!!organizationTitle;

	const hideAnyPrepopulatedFields =
		hidePrepopulatedIdentityFields ||
		hidePrepopulatedPhoneField ||
		hidePrepopulatedOrganizationTitleField;

	const isPasswordOnlyView =
		props.isInitialView &&
		hidePrepopulatedIdentityFields &&
		hidePrepopulatedPhoneField &&
		hidePrepopulatedOrganizationTitleField;

	return (
		<Fragment>
			<h3>
				{props.isInitialView
					? `Welcome${firstName ? ", " + firstName : ""}! Let's set up your account!`
					: "Create or confirm your account"}
			</h3>
			<p>
				{props.isInitialView
					? isPasswordOnlyView
						? `Create a password for ${email} to continue`
						: "Complete the information below to continue"
					: "Did you recieve an invite? Register below to continue"}
			</p>

			{error && <div className="error-message">{error}</div>}

			<form
				onSubmit={handleSubmit}
				className="login-form"
				autoComplete="on"
				style={{
					maxWidth: showPmsSidePanel ? "900px" : undefined,
					position: "relative",
				}}>
				{isLoadingPmsOptions && <LoadingSpinner overlay centered />}
				<div
					style={{
						display: "flex",
						gap: "30px",
						alignItems: "flex-start",
						flexWrap: "wrap",
					}}>
					<div style={{ flex: 1, minWidth: "300px" }}>
						<div className="form-row">
							{!hidePrepopulatedIdentityFields && (
								<>
									<div className="form-group">
										<input
											type="text"
											id="firstName"
											value={firstName}
											placeholder=" "
											onChange={(e) => setFirstName(e.currentTarget.value)}
											required
											disabled={isSubmitting}
										/>
										<label htmlFor="firstName">First Name*</label>
									</div>

									<div className="form-group">
										<input
											type="text"
											id="lastName"
											value={lastName}
											placeholder=" "
											onChange={(e) => setLastName(e.currentTarget.value)}
											required
											disabled={isSubmitting}
										/>
										<label htmlFor="lastName">Last Name*</label>
									</div>
								</>
							)}
						</div>

						{!hidePrepopulatedIdentityFields && (
							<div className="form-group">
								<input
									type="email"
									id="email"
									value={email}
									placeholder=" "
									onChange={(e) => handleEmailInput(e.currentTarget.value)}
									required
									disabled={isSubmitting}
									autoComplete="email"
								/>
								<label htmlFor="email">Email Address*</label>

								{email && !isEmailValid() && (
									<div className="field-error">
										Please enter a valid email address
									</div>
								)}
							</div>
						)}

						{!hidePrepopulatedPhoneField && (
							<div className="form-group">
								<input
									type="tel"
									id="phoneNumber"
									value={phoneNumber}
									placeholder=" "
									onChange={handlePhoneInput}
									disabled={isSubmitting}
									autoComplete="tel"
								/>
								<label htmlFor="phoneNumber">Mobile Phone Number</label>
							</div>
						)}

						<div className="form-group">
							<input
								type="password"
								id="password"
								value={password}
								placeholder=" "
								onChange={(e) => setPassword(e.currentTarget.value)}
								required
								disabled={isSubmitting}
							/>
							<label htmlFor="password">Password*</label>
							{password && !isValidPassword(password) && (
								<div className="field-error">
									Password must be at least 8 characters
								</div>
							)}
						</div>

						<div className="form-group">
							<input
								type="password"
								id="confirmPassword"
								value={confirmPassword}
								placeholder=" "
								onChange={(e) => setConfirmPassword(e.currentTarget.value)}
								required
								disabled={isSubmitting}
							/>
							<label htmlFor="confirmPassword">Confirm Password*</label>
							{password &&
								confirmPassword &&
								!passwordsMatch(password, confirmPassword) && (
									<div className="field-error">Passwords do not match</div>
								)}
						</div>

						{createOrganization && (
							<Fragment>
								{!hidePrepopulatedOrganizationTitleField && (
									<div className="form-group">
										<input
											type="text"
											id="organizationTitle"
											value={organizationTitle}
											placeholder=" "
											onChange={(e) =>
												setOrganizationTitle(e.currentTarget.value)
											}
											disabled={isSubmitting}
										/>
										<label htmlFor="organizationTitle">
											Organization Title
										</label>
									</div>
								)}

								{!hidePmsSelect && (
									<div className="form-group">
										<label htmlFor="importPms" className="static-label">
											Create properties
											{getSelectedPms() &&
												getSelectedPms().instructions_link && (
													<span
														style={{
															marginLeft: "10px",
															fontSize: "12px",
															fontWeight: "normal",
														}}>
														<a
															href={getSelectedPms().instructions_link}
															target="_blank"
															rel="noopener noreferrer"
															style={{
																color: "#e30b5d",
																textDecoration: "underline",
															}}>
															Integration Instructions
														</a>
													</span>
												)}
										</label>
										<select
											id="importPms"
											value={pmsProvider}
											onChange={(e) => setPmsProvider(e.currentTarget.value)}
											disabled={isSubmitting}>
											<option value="no">Not at this time</option>
											{pmsOptions.length > 0 ? (
												pmsOptions.map((pms) => (
													<option key={pms.name} value={pms.name}>
														{pms.title}
													</option>
												))
											) : (
												<option value="barefoot">Import from Barefoot</option>
											)}
											<option value="auto_generate">Autogenerate</option>
										</select>
									</div>
								)}

								{pmsProvider === "auto_generate" && (
									<Fragment>
										{autogenerateItems.length > 0 && (
											<div className="form-group">
												<label className="static-label">Added Properties</label>
												<ul
													className="added-items-list"
													style={{ listStyle: "none", padding: 0 }}>
													{autogenerateItems.map((item, index) => {
														const displayParts = [];
														if (item.address1)
															displayParts.push(
																`${item.address1}, ${item.city}`,
															);
														if (item.airbnbUrl) displayParts.push("Airbnb");
														if (item.vrboUrl) displayParts.push("VRBO");

														return (
															<li
																key={index}
																style={{
																	marginBottom: "5px",
																	display: "flex",
																	alignItems: "center",
																	justifyContent: "space-between",
																	background: "#f5f5f5",
																	padding: "5px 10px",
																	borderRadius: "4px",
																}}>
																<span>{displayParts.join(" | ")}</span>
																<button
																	type="button"
																	onClick={() => handleRemoveItem(index)}
																	style={{
																		marginLeft: "10px",
																		cursor: "pointer",
																		border: "none",
																		background: "none",
																		fontSize: "18px",
																		color: "#d9534f",
																	}}>
																	&minus;
																</button>
															</li>
														);
													})}
												</ul>
											</div>
										)}

										<div className="form-group">
											{!showAddItem && !hsId && (
												<button
													type="button"
													className="button-small"
													style={{
														background: "none",
														border: "none",
														color: "#007bff",
														cursor: "pointer",
														fontSize: "14px",
														padding: "5px 0",
														display: "flex",
														alignItems: "center",
													}}
													onClick={() => {
														setShowAddItem(true);
														setAddItemError("");
													}}>
													<span
														style={{ fontSize: "18px", marginRight: "5px" }}>
														+
													</span>{" "}
													Add Property
												</button>
											)}
										</div>
									</Fragment>
								)}

								{pmsProvider === "barefoot" && !getSelectedPms() && (
									<div className="form-group">
										<input
											type="text"
											id="clientCode"
											value={clientCode}
											placeholder=" "
											onChange={(e) => setClientCode(e.currentTarget.value)}
											required
											disabled={isSubmitting}
										/>
										<label htmlFor="clientCode">Client Code</label>
									</div>
								)}

								{hideAnyPrepopulatedFields && (
									<div>
										<button
											type="button"
											onClick={() => setShowAllFields(true)}
											className="btn btn-link muted"
											style={{ padding: 0, fontSize: "12px" }}>
											Review your account information
										</button>
									</div>
								)}
							</Fragment>
						)}
					</div>
					{showPmsSidePanel && (
						<div
							style={{
								flex: 1,
								minWidth: "300px",
								marginTop: "0",
							}}>
							<div
								style={{
									border: "1px solid #e0e0e0",
									borderRadius: "8px",
									backgroundColor: "#fff",
									boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
									overflow: "hidden",
								}}>
								{getSelectedPms() ? (
									<Fragment>
										<div
											style={{
												display: "flex",
												borderBottom: "1px solid #e0e0e0",
												backgroundColor: "#f9f9f9",
											}}>
											<button
												type="button"
												onClick={() => setActiveTab("required")}
												style={{
													flex: 1,
													padding: "12px",
													background:
														activeTab === "required" ? "#fff" : "transparent",
													border: "none",
													borderBottom:
														activeTab === "required"
															? "2px solid #0056b3"
															: "none",
													fontWeight: activeTab === "required" ? "600" : "400",
													color: activeTab === "required" ? "#0056b3" : "#555",
													cursor: "pointer",
													outline: "none",
												}}>
												Required
											</button>
											<button
												type="button"
												onClick={() => setActiveTab("advanced")}
												style={{
													flex: 1,
													padding: "12px",
													background:
														activeTab === "advanced" ? "#fff" : "transparent",
													border: "none",
													borderBottom:
														activeTab === "advanced"
															? "2px solid #0056b3"
															: "none",
													fontWeight: activeTab === "advanced" ? "600" : "400",
													color: activeTab === "advanced" ? "#0056b3" : "#555",
													cursor: "pointer",
													outline: "none",
												}}>
												Advanced
											</button>
										</div>

										<div style={{ padding: "20px" }}>
											{getSelectedPms().instructions_link &&
												activeTab === "required" && (
													<div
														style={{
															marginBottom: "20px",
															padding: "12px",
															backgroundColor: "#fff3cd",
															border: "1px solid #ffeeba",
															borderRadius: "4px",
															display: "flex",
															alignItems: "center",
														}}>
														<svg
															width="24"
															height="24"
															viewBox="0 0 24 24"
															fill="none"
															stroke="currentColor"
															strokeWidth="2"
															strokeLinecap="round"
															strokeLinejoin="round"
															style={{ marginRight: "10px", color: "#856404" }}>
															<path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
															<line x1="12" y1="9" x2="12" y2="13"></line>
															<line x1="12" y1="17" x2="12.01" y2="17"></line>
														</svg>
														<a
															href={getSelectedPms().instructions_link}
															target="_blank"
															rel="noopener noreferrer"
															style={{
																fontSize: "14px",
																fontWeight: "bold",
																color: "#856404",
																textDecoration: "underline",
															}}>
															View Integration Instructions &rarr;
														</a>
													</div>
												)}

											{renderPmsFields(activeTab === "required")}
										</div>
									</Fragment>
								) : (
									renderAddItemForm()
								)}
							</div>
						</div>
					)}
				</div>
				{phoneNumber && (
					<p className="small disclaimer">
						<sup>*</sup>By providing your phone number, you agree to receive
						SMS/text messages from us. Standard msg & data rates may apply.
					</p>
				)}

				<div className="form-actions">
					<button
						type="submit"
						className="submit-button"
						disabled={!isFormValid() || isSubmitting}>
						{isSubmitting ? "Creating Account..." : "Create Account"}
					</button>
				</div>

				<div className="form-links">
					<a
						href="#"
						className="form-text-link"
						onClick={(e) => {
							e.preventDefault();
							props.onCancel();
						}}>
						Log into your account
					</a>
				</div>
			</form>
		</Fragment>
	);
}

export default CreateAccount;
